import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import { brand, radius, shadow } from "../../theme/businessFlowsTheme";
import { useViewConfig } from "../app/ViewConfigContext";
import TypeIcon from "../app/typeIcons";
import { attributeLabel, displayValue, fieldsFor, formatLabel, setting, tint, typeInfo } from "../../utils/viewConfig";
import { sortFormats } from "../diagram/formatRoute";
import { SHOW_MAPS } from "../../features";

const clampLines = (lines) => ({ display: "-webkit-box", WebkitLineClamp: lines, WebkitBoxOrient: "vertical", overflow: "hidden" });

export default function ResultCard({ item, path, selected, starred, onSelect, onToggleStar, onOpenExplorer }) {
  const config = useViewConfig();
  const [expanded, setExpanded] = useState(false);
  const info = typeInfo(config, item.Type);

  // What this object can actually be opened as: a diagram it has (or, for a
  // diagram node, the diagram it is), else the map of the objects beneath it.
  // Never offered on a guess from the type.
  const diagramOf = item._diagramOf || null;
  // Each stored diagram is a choice of its own, named after what it is - BPMN,
  // SVG, Mermaid, PDF - in the app's own order (product owner, 28 Sep 2026:
  // never a choice called "Diagram" where the type is known). A diagram node
  // is one format: the one it holds.
  const storedFormats = sortFormats(
    (diagramOf ? [item._diagramFormat] : Array.isArray(item.formats) ? item.formats : []).filter(Boolean)
  );
  const hasDiagram = storedFormats.length > 0 || Boolean(diagramOf);
  // SHOW_MAPS=false: structure maps are hidden as a user-facing thing, so an
  // object with no diagram falls through to "Explore" (relationships) below.
  const hasMap = SHOW_MAPS && !hasDiagram && Boolean(item.hasAssignment);
  const opensDiagram = hasDiagram || hasMap;

  // The fields the admin chose for cards of this type, else everything it carries.
  const fields = fieldsFor(config, "card", item);
  const description = fields.includes("Description") ? displayValue(item.Description) : "";
  const others = fields.filter((key) => key !== "Description");
  const limit = setting(config, "card.fieldLimit");
  const listed = expanded ? others : others.slice(0, limit);
  const hiddenCount = others.length - listed.length;
  const longDescription = description.length > setting(config, "card.descriptionLength");
  const canExpand = longDescription || hiddenCount > 0 || (expanded && others.length > limit);

  // A click anywhere on the card picks it: the Details button was the only way
  // in, and nobody found it. The buttons inside keep their own jobs - each one
  // stops the click from reaching the card (`pick` below), so pressing Explore
  // explores and nothing else happens.
  const pick = (handler) => (event) => {
    event.stopPropagation();
    if (handler) handler(event);
  };

  return (
    <Box
      // The panel beside the list always shows one of these results - the one
      // the reader picked, or the first until they do. Which one it is must be
      // readable, not only visible: the mark is said as well as drawn.
      aria-current={selected ? "true" : undefined}
      role="button"
      tabIndex={0}
      // Children of a button are presentational, so the card says its own name.
      aria-label={item.name || info.label}
      onClick={onSelect}
      // Only when the card itself has focus: Enter on a button inside it is
      // that button's press, and must not select the card as well.
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        if (onSelect) onSelect(event);
      }}
      sx={{
        position: "relative",
        cursor: "pointer",
        textAlign: "left",
        "&:focus-visible": { outline: `2px solid ${brand.ink}`, outlineOffset: 2 },
        p: { xs: 1.75, md: 2 },
        pl: { xs: 2.25, md: 2.5 },
        borderRadius: radius.card,
        bgcolor: "#fff",
        border: `1px solid ${selected ? brand.ink : brand.line}`,
        boxShadow: selected ? shadow.picked : "none",
        transition: "border-color .15s ease, box-shadow .15s ease",
        "&:hover": { borderColor: selected ? brand.ink : brand.inkSoft },
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 12,
          bottom: 12,
          width: 4,
          borderRadius: "0 4px 4px 0",
          bgcolor: selected ? brand.signal : "transparent",
        },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            width: 36,
            height: 36,
            flexShrink: 0,
            borderRadius: radius.control,
            display: "grid",
            placeItems: "center",
            bgcolor: opensDiagram ? brand.ink : tint(info.colour, 0.12),
            color: opensDiagram ? brand.signal : info.colour,
            mt: 0.25,
          }}
        >
          {hasDiagram ? (
            <AccountTreeRoundedIcon fontSize="small" />
          ) : hasMap ? (
            <GridViewRoundedIcon fontSize="small" />
          ) : (
            <TypeIcon name={info.icon} fontSize="small" />
          )}
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
            <Box sx={{ minWidth: 0 }}>
              {item.Type && (
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: info.colour, flexShrink: 0 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {info.label}
                  </Typography>
                </Stack>
              )}
              <Typography component="h3" sx={{ fontWeight: 650, fontSize: "1rem", lineHeight: 1.35, overflowWrap: "anywhere" }}>
                {item.name || info.label}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0, mt: -0.5, mr: -0.75 }}>
              {item.URL && (
                <Tooltip title="Open linked document">
                  <IconButton size="small" component={RouterLink} to={`${path}/external-link/${item.URL}`} target="_blank" onClick={pick()} aria-label="Open linked document">
                    <LinkRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={starred ? "Remove from favourites" : "Add to favourites"}>
                <IconButton
                  size="small"
                  onClick={pick(onToggleStar)}
                  aria-pressed={starred}
                  aria-label={starred ? "Remove from favourites" : "Add to favourites"}
                  sx={{ color: starred ? brand.signalDeep : "text.secondary" }}
                >
                  {starred ? <StarRoundedIcon fontSize="small" /> : <StarBorderRoundedIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          {description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.75, lineHeight: 1.5, overflowWrap: "anywhere", ...(expanded ? {} : clampLines(2)) }}
            >
              {description}
            </Typography>
          )}

          {listed.length > 0 && (
            <Box component="dl" sx={{ m: 0, mt: 1, display: "grid", gridTemplateColumns: "auto minmax(0, 1fr)", columnGap: 1.5, rowGap: 0.25 }}>
              {listed.map((key) => (
                <React.Fragment key={key}>
                  <Typography component="dt" variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {attributeLabel(key)}
                  </Typography>
                  <Typography component="dd" variant="caption" sx={{ m: 0, overflowWrap: "anywhere", ...(expanded ? {} : clampLines(1)) }}>
                    {displayValue(item[key])}
                  </Typography>
                </React.Fragment>
              ))}
            </Box>
          )}

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.25, flexWrap: "wrap", rowGap: 0.75 }}>
            {/* Every way of looking at this object is offered: its diagram or
                map, its relationships, and its properties. */}
            {hasDiagram &&
              (storedFormats.length ? storedFormats : [null]).map((format) => (
                <Button
                  key={format || "diagram"}
                  size="small"
                  variant="contained"
                  component={RouterLink}
                  to={`${path}/map/${diagramOf || item.GUID}${format ? `?format=${encodeURIComponent(format)}` : ""}`}
                  target="_blank"
                  onClick={pick()}
                  startIcon={<AccountTreeRoundedIcon fontSize="small" />}
                >
                  {/* The format's own name, as this database calls it. Only a
                      diagram whose format nothing recorded reads "Open
                      diagram" - there is no type to name it by. */}
                  {format ? `Open ${formatLabel(config, format)}` : "Open diagram"}
                </Button>
              ))}
            {!hasDiagram && hasMap && (
              <Button
                size="small"
                variant="contained"
                component={RouterLink}
                to={`${path}/map/${item.GUID}`}
                target="_blank"
                onClick={pick()}
                startIcon={<GridViewRoundedIcon fontSize="small" />}
              >
                Open map
              </Button>
            )}
            <Button size="small" variant={opensDiagram ? "outlined" : "contained"} onClick={pick(onOpenExplorer)} startIcon={<HubRoundedIcon fontSize="small" />}>
              Explore
            </Button>
            {/* The same thing a click on the card does, said in words - on a
                phone, where the panel is a drawer, this is the obvious way in. */}
            <Button size="small" variant={selected ? "outlined" : "text"} onClick={pick(onSelect)} startIcon={<InfoOutlinedIcon fontSize="small" />}>
              Details
            </Button>
            {canExpand && (
              <Chip
                size="small"
                variant="outlined"
                label={expanded ? "Show less" : hiddenCount > 0 ? `Show ${hiddenCount} more` : "Show more"}
                onClick={pick(() => setExpanded((v) => !v))}
                sx={{ borderColor: brand.line }}
              />
            )}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}
